import { Button } from '@repo/ui/button'
import { Card, CardContent } from '@repo/ui/card'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@repo/ui/command'
import { DataProvider } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { Label } from '@repo/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/popover'
import { Textarea } from '@repo/ui/textarea'
import { cn } from '@repo/ui/utils'
import { CheckIcon, ChevronsUpDown, CopyIcon, Loader2, PrinterIcon } from 'lucide-react'
import { ReactNode, useEffect, useState } from 'react'
import { IoSparklesSharp } from 'react-icons/io5'
import { remark } from 'remark'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import remarkHtml from 'remark-html'

import { useReportGeneration } from '@/components/beneficiary/report/hooks/useReportGeneration'
import { ReportDisplay } from '@/components/beneficiary/report/ReportDisplay'
import { ReportTemplateCreator } from '@/components/beneficiary/report/ReportTemplateCreator'
import { ReportTemplateEditor } from '@/components/beneficiary/report/ReportTemplateEditor'
import { ItemCreatedDate, ItemUpdatedDate } from '@/components/CollapsibleCard'
import { useCopyToClipboard } from '@/hooks'
import { Beneficiary } from '@/store/beneficiary'
import { getCurrentBeneficiary } from '@/store/helpers'
import { useStoreDispatch } from '@/store/hooks'
import { updateReportTemplate } from '@/store/report-templates/thunks'
import { ReportTemplate } from '@/store/report-templates/types'

export function ReportGenerator({ reportTemplates }: { reportTemplates: ReportTemplate[] }) {
  const dispatch = useStoreDispatch()
  const { tc, t } = useT({ keyPrefix: 'reports' })

  const [selectedId, setSelectedId] = useState<ReportTemplate['id'] | undefined>(undefined)

  const [selectedReportTemplate, setSelectedReportTemplate] = useState<ReportTemplate | undefined>(
    undefined,
  )

  // Not saved
  const [tempInstructions, setTempInstructions] = useState('')

  useEffect(() => {
    if (selectedId) {
      const reportTemplate = reportTemplates.find((template) => template.id === selectedId)
      if (!reportTemplate) {
        // reset if not found
        setSelectedId(undefined)
        setSelectedReportTemplate(undefined)
        setTempInstructions('')
        return
      }

      setSelectedReportTemplate(reportTemplate)
      setTempInstructions(reportTemplate?.instructions || '')
    } else if (reportTemplates.length > 0) {
      // initDefaultValues
      const defaultReportTemplate = reportTemplates[0]!
      setSelectedId(defaultReportTemplate.id)
    }
  }, [selectedId, reportTemplates])

  const { isGenerating, generateReport, report, clearReport, Trace } = useReportGeneration()
  useEffect(clearReport, [selectedId])

  const generate = () => {
    if (!selectedId) return
    void generateReport(tempInstructions)
  }

  const handleSaveInstructions = () => {
    if (!selectedReportTemplate) return
    void dispatch(
      updateReportTemplate({
        reportTemplateId: selectedReportTemplate.id,
        fields: { instructions: tempInstructions },
      }),
    )
  }
  const handleResetInstructions = () => {
    if (!selectedReportTemplate) return
    setTempInstructions(selectedReportTemplate.instructions || '')
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      <ReportSelector
        isGenerating={isGenerating}
        onSelect={setSelectedId}
        reportTemplates={reportTemplates}
        selectedReportTemplate={selectedReportTemplate}
      >
        <div className="flex flex-1 gap-2">
          {selectedReportTemplate && (
            <ReportTemplateEditor
              isDisabled={isGenerating}
              reportTemplate={selectedReportTemplate}
            />
          )}
          <Button variant="highlight" disabled={isGenerating || !selectedId} onClick={generate}>
            {isGenerating ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <IoSparklesSharp className="size-4" />
            )}
            {tc(isGenerating ? 'generating' : 'generate', { cfl: true })}
          </Button>
        </div>
        <ReportTemplateCreator isDisabled={isGenerating} onCreated={(id) => setSelectedId(id)} />
      </ReportSelector>
      {selectedId && (
        <div className="flex flex-col gap-2">
          <ReportInstructions
            isDisabled={isGenerating}
            value={tempInstructions}
            onChange={(instructions) => setTempInstructions(instructions)}
          >
            {selectedReportTemplate && tempInstructions !== selectedReportTemplate.instructions && (
              <Label className="text-gray-500">
                ({t('changesDetected')}{' '}
                <div
                  onClick={handleSaveInstructions}
                  className="inline cursor-pointer text-gray-500 underline underline-offset-2 transition-colors hover:text-gray-600"
                >
                  {tc('save', { cfl: true })}
                </div>{' '}
                {tc('or')}{' '}
                <div
                  onClick={handleResetInstructions}
                  className="inline cursor-pointer text-gray-500 underline underline-offset-2 transition-colors hover:text-gray-600"
                >
                  {tc('reset', { cfl: true })}
                </div>
                )
              </Label>
            )}
          </ReportInstructions>
          {selectedReportTemplate && (
            <div>
              <div className="flex items-end justify-between pb-2">
                <Label className="text-base">{t('generatedReport')}</Label>
                <div className="flex gap-2">
                  <CopyButton report={report} selectedReportTemplate={selectedReportTemplate} />

                  <DataProvider
                    selectors={[getCurrentBeneficiary]}
                    render={(beneficiary) => (
                      <PrintButton
                        report={report}
                        reportName={selectedReportTemplate.name}
                        beneficiary={beneficiary}
                      />
                    )}
                  />
                </div>
              </div>
              <Card variant="passive">
                <CardContent>
                  <ReportDisplay
                    report={report}
                    isLoading={isGenerating}
                    reportTemplateName={selectedReportTemplate.name}
                  />
                  <Trace />
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function CopyButton({
  report,
  selectedReportTemplate,
}: {
  report: string
  selectedReportTemplate: ReportTemplate | undefined
}) {
  const { tc } = useT()
  const { copy: handleCopyToClipboard, isCopied } = useCopyToClipboard(
    tc('successfullyCopied', { value: selectedReportTemplate?.name || '' }),
  )
  return (
    <Button
      variant="outline"
      disabled={!report || isCopied}
      onClick={() => void handleCopyToClipboard(report)}
    >
      <CopyIcon className="size-4" />
      {tc('copy', { cfl: true })}
    </Button>
  )
}

function PrintButton({
  beneficiary,
  reportName,
  report,
}: {
  beneficiary: Beneficiary
  reportName: string
  report: string
}) {
  const { tc } = useT()

  const handlePrint = async () => {
    if (!report) return
    const htmlContent = await remark()
      .use(remarkBreaks)
      .use(remarkGfm)
      .use(remarkHtml)
      .process(report)

    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${beneficiary.fullName} - ${reportName}</title>
            <link rel="stylesheet" href="https://unpkg.com/@tailwindcss/typography@0.5.10/dist/typography.min.css">
            <style>
              @page {
                margin: 2cm;
              }
              
              body {
                font-family: system-ui, -apple-system, sans-serif;
                line-height: 1.6;
                color: #000;
                max-width: 100%;
              }
              
              table {
                border-collapse: collapse;
                width: 100%;
                margin: 1em 0;
              }
              
              th, td {
                border: 1px solid #d1d5db;
                padding: 0.5em 0.75em;
                text-align: left;
              }
              
              th {
                background-color: #f3f4f6;
                font-weight: 600;
              }
              
              table td:first-child:nth-last-child(2),
              table td:first-child:nth-last-child(2) ~ td,
              table th:first-child:nth-last-child(2),
              table th:first-child:nth-last-child(2) ~ th {
                width: 50%;
              }
              
              @media print {
                body {
                  print-color-adjust: exact;
                  -webkit-print-color-adjust: exact;
                }
                
                table {
                  page-break-inside: auto;
                }
                
                tr {
                  page-break-inside: avoid;
                  page-break-after: auto;
                }
              }
            </style>
          </head>
          <body class="prose prose-sm max-w-none">
            ${String(htmlContent)}
          </body>
        </html>
      `)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
    }
  }
  return (
    <Button variant="outline" disabled={!report} onClick={() => void handlePrint()}>
      <PrinterIcon className="size-4" />
      {tc('print', { cfl: true })}
    </Button>
  )
}

function ReportSelector({
  children,
  isGenerating,
  onSelect,
  reportTemplates,
  selectedReportTemplate,
}: {
  children: ReactNode
  isGenerating: boolean
  onSelect: (id: string) => void
  reportTemplates: ReportTemplate[]
  selectedReportTemplate: ReportTemplate | undefined
}) {
  const { tc } = useT({ keyPrefix: 'reports' })
  const [open, setOpen] = useState(false)
  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <div className="flex items-end gap-2">
        <div className="flex flex-col gap-2">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="w-fit min-w-96 justify-between text-base"
              defaultValue={selectedReportTemplate?.name}
              disabled={isGenerating || reportTemplates.length === 0}
              onClick={() => setOpen(true)}
            >
              {selectedReportTemplate ? (
                <span className="font-medium">{selectedReportTemplate.name}</span>
              ) : (
                <>{tc('select', { cfl: true })}</>
              )}
              <ChevronsUpDown className="size-4 opacity-50" />
            </Button>
          </PopoverTrigger>
        </div>
        {children}
      </div>

      <PopoverContent className="w-96 p-0">
        <Command>
          <CommandInput placeholder={tc('search', { cfl: true })} />
          <CommandList>
            <CommandEmpty>{tc('result', { cfl: true, count: 0 })}</CommandEmpty>
            <CommandGroup>
              {reportTemplates.map((reportTemplate) => (
                <CommandItem
                  key={reportTemplate.id}
                  keywords={[reportTemplate.name]}
                  value={reportTemplate.id}
                  onSelect={(id) => {
                    onSelect(id)
                    setOpen(false)
                  }}
                  className={cn(
                    'text-sm',
                    selectedReportTemplate?.id === reportTemplate.id && 'text-primary-500',
                  )}
                >
                  <ReportTemplatePreview reportTemplate={reportTemplate} />
                  <CheckIcon
                    className={cn(
                      'text-primary-500 ml-auto size-6',
                      selectedReportTemplate?.id === reportTemplate.id ? 'block' : 'hidden',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function ReportTemplatePreview({ reportTemplate }: { reportTemplate: ReportTemplate }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-base font-medium">{reportTemplate.name}</span>
      <span className="text-sm">{reportTemplate.description}</span>
      <ItemCreatedDate className="text-xs" createdAt={reportTemplate.createdAt} />
      <ItemUpdatedDate className="text-xs" updatedAt={reportTemplate.updatedAt} />
    </div>
  )
}

function ReportInstructions({
  children,
  isDisabled,
  onChange,
  value,
}: {
  children?: ReactNode
  isDisabled: boolean
  onChange: (value: string) => void
  value: string
}) {
  const { tc, t } = useT({ keyPrefix: 'reports' })
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Label htmlFor="instructions" className="w-fit text-base">
          {tc('instructions', { cfl: true, colon: true })}
        </Label>
        {children}
      </div>
      <Textarea
        id="instructions"
        placeholder={t('instructions.placeholder', { cfl: true })}
        value={value}
        onChange={({ target }) => onChange(target.value)}
        rows={10}
        disabled={isDisabled}
        className="resize-none text-base"
      />
    </div>
  )
}
